// Analyst Co-Pilot — the portal Analyst tab (text chat).
//
// A chat with the read-only data Analyst, reached through the profile-LOCKED
// `/api/analyst-copilot` door (the backend forces the `analyst_portal` profile —
// the un-re-skinned variant of the console `analyst`, so it's the SAME agent,
// just a focused portal UI). Text-only: no voice, no mail panel. Reuses the Agent
// Console transcript renderer (AssistantTurn), model picker, and the shared
// conversation-history drawer end-to-end, plus the SSE wire contract.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { AgentFileCard } from '../components/agentConsole/AgentFileCard';
import { AssistantTurn } from '../components/agentConsole/AssistantTurnView';
import { MessageTimestamp } from '../components/agentConsole/MessageTimestamp';
import ModelSelect from '../components/agentConsole/ModelSelect';
import { ConversationHistoryDrawer } from '../components/agentConsole/ConversationHistoryDrawer';
import { makeActiveConvStore } from '../components/agentConsole/conversationHistory';
import { AttachmentTray, DropVeil } from '../components/chat/AttachmentTray';
import { MessageAttachmentChips } from '../components/chat/MessageAttachmentChips';
import { effectiveMimeType } from '../components/chat/composerAttachments';
import type { MessageAttachment } from '../components/chat/messageAttachments';
import { useComposerAttachments } from '../components/chat/useComposerAttachments';
import { useAuth } from '../context/AuthContext';
import { useGuruWidgetViewContext } from '../hooks/useGuruWidgetViewContext';
import {
  detailToAnalystTurns,
  reduceTurn,
  type AssistantTurnModel,
  type Turn,
} from './analystTurns';
import {
  createConversation,
  getConversationDetail,
  getModels,
  listConversations,
  streamAnalystMessage,
  type ConversationRead,
  type ModelOption,
} from '../services/analystCopilot';
import {
  turnSetupErrorMessage,
  uploadConversationAttachment,
} from '../services/agentConsole';
import styles from './AnalystCopilotPage.module.css';

// The Analyst door's API prefix (matches services/analystCopilot BASE).
const ANALYST_BASE = '/analyst-copilot';

// Per-user store for the active Analyst thread so a refresh resumes it.
const activeConvStore = makeActiveConvStore('cm_analyst_convid_');

export default function AnalystCopilotPage() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Selectable models + this door's (Analyst) default — the picker preselects the
  // default; the chosen model rides on the conversation (last-turn-wins).
  const [models, setModels] = useState<ModelOption[]>([]);
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const convIdRef = useRef<string | null>(null);
  const idCounter = useRef(0);
  const logRef = useRef<HTMLDivElement>(null);

  // Files staged for the next message: drag-drop / paste / picker (shared hook).
  // Mirrored in a ref so the async send() closure reads the latest staged list.
  const attach = useComposerAttachments({ disabled: sending });
  const stagedRef = useRef<File[]>([]);
  stagedRef.current = attach.files;

  // Conversation history (shared drawer): the caller's own Analyst threads,
  // remembered per user so a refresh resumes the last one.
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const ownerName = user?.name ?? null;
  const ownerInitials =
    (user?.name || user?.email || '').trim().slice(0, 2).toUpperCase() || null;
  const [conversations, setConversations] = useState<ConversationRead[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Tell the Guru feedback widget which AI conversation is on screen.
  useGuruWidgetViewContext('analyst_copilot_chat', {
    copilot: 'analyst-copilot',
    conversation_id: activeId ?? convIdRef.current ?? undefined,
    model_id: selectedModelId || undefined,
  });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const restoredRef = useRef(false);

  const nextId = () => `t${idCounter.current++}`;

  // Follow the stream: pin to the bottom whenever the transcript grows.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // Load the selectable models once (non-fatal): the picker just doesn't render.
  useEffect(() => {
    let cancelled = false;
    getModels()
      .then((data) => {
        if (cancelled) return;
        setModels(data.models);
        setDefaultModel(data.default_model);
        setSelectedModelId((prev) => prev || data.default_model);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const patchAssistant = useCallback(
    (id: string, fn: (t: AssistantTurnModel) => AssistantTurnModel) => {
      setTurns((prev) =>
        prev.map((t) => (t.id === id && t.role === 'assistant' ? fn(t) : t)),
      );
    },
    [],
  );

  // Load the caller's own Analyst threads for the history drawer (non-fatal).
  const refreshHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      setConversations(await listConversations({ limit: 50 }));
    } catch {
      /* non-fatal — the drawer just shows what it already has */
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const openHistory = useCallback(() => {
    setDrawerOpen(true);
    void refreshHistory();
  }, [refreshHistory]);

  // Resume a saved thread: rebuild its transcript (shared detailToAnalystTurns)
  // and make it the active conversation.
  const loadConversation = useCallback(
    async (id: string) => {
      if (sending) return;
      setDrawerOpen(false);
      setError(null);
      convIdRef.current = id;
      setActiveId(id);
      activeConvStore.write(userId, id);
      try {
        const detail = await getConversationDetail(id);
        setTurns(detailToAnalystTurns(detail));
      } catch {
        setError('Could not load that conversation — please try again.');
      }
    },
    [sending, userId],
  );

  // Restore the last thread on mount: a ?conversation= deep link wins, else the
  // per-user remembered id. Runs once, only after we know who the user is.
  useEffect(() => {
    if (restoredRef.current || !userId) return;
    restoredRef.current = true;
    const deepLink = new URLSearchParams(window.location.search).get('conversation');
    const resumeId = deepLink || activeConvStore.read(userId);
    if (resumeId) void loadConversation(resumeId);
  }, [userId, loadConversation]);

  const send = useCallback(
    async (raw: string) => {
      const prompt = raw.trim();
      if ((!prompt && stagedRef.current.length === 0) || sending) return;
      setError(null);
      setSending(true);

      // Snapshot + clear the files staged for this turn (they upload below).
      const staged = stagedRef.current;
      if (staged.length > 0) attach.clear();
      // A screenshot pasted with no words still sends — the model-facing prompt
      // gains the attachment block server-side either way.
      const outgoing = prompt || '(see attached file(s))';
      const localChips: MessageAttachment[] = staged.map((f, i) => ({
        id: `local-${i}-${f.name}`,
        filename: f.name,
        mimeType: effectiveMimeType(f),
        sizeBytes: f.size,
      }));

      const asstId = nextId();
      const sentAt = new Date().toISOString();
      setTurns((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'user',
          text: outgoing,
          createdAt: sentAt,
          attachments: localChips.length ? localChips : undefined,
        },
        {
          id: asstId,
          role: 'assistant',
          blocks: [],
          streaming: true,
          createdAt: sentAt,
        },
      ]);

      try {
        let cid = convIdRef.current;
        if (!cid) {
          const convo = await createConversation({
            model: selectedModelId || undefined,
          });
          cid = convo.id;
          convIdRef.current = cid;
          setActiveId(cid);
          activeConvStore.write(userId, cid);
        }
        // Upload the staged files BEFORE the turn so the backend can hand them
        // to Merlin (materialized into the turn's working dir for the Read
        // tool). One failed upload doesn't block the turn.
        for (const file of staged) {
          try {
            await uploadConversationAttachment(ANALYST_BASE, cid, file);
          } catch {
            /* non-fatal — the turn still runs */
          }
        }
        await streamAnalystMessage(
          cid,
          prompt,
          (evt) => {
            if (evt.type === 'error' || evt.type === 'cancelled') {
              setError(evt.message || 'Something went wrong — please try again.');
              patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
              return;
            }
            if (evt.type === 'state' || evt.type === 'done') {
              patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
              return;
            }
            // Fold every other frame into the turn's block trace (thinking /
            // tool / answer).
            patchAssistant(asstId, (t) => ({ ...t, ...reduceTurn(t, evt) }));
          },
          undefined,
          selectedModelId || undefined,
        );
      } catch (e) {
        setError(turnSetupErrorMessage(e, 'Could not reach the Analyst.'));
      } finally {
        patchAssistant(asstId, (t) => ({ ...t, streaming: false }));
        setSending(false);
      }
    },
    [sending, patchAssistant, selectedModelId, userId, attach],
  );

  const submitText = useCallback(() => {
    const v = input;
    setInput('');
    void send(v);
  }, [input, send]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitText();
    }
  };

  const startNew = () => {
    if (sending) return;
    convIdRef.current = null;
    setActiveId(null);
    activeConvStore.write(userId, null);
    setTurns([]);
    setError(null);
    setDrawerOpen(false);
  };

  return (
    <div className={styles.page}>
      <div className={styles.stage}>
        <section className={styles.chat} aria-label="Merlin chat">
          <header className={styles.chatHead}>
            <span className={styles.dot} aria-hidden="true" />
            <div className={styles.headTitle}>
              <span className={styles.chatTitle}>Merlin</span>
              <span className={styles.chatMeta}>
                Analyst · read-only data · explores orders, customers, invoices &amp; Sage
              </span>
            </div>
            <div className={styles.controls}>
              {models.length > 0 && (
                <ModelSelect
                  models={models}
                  value={selectedModelId || defaultModel}
                  onChange={setSelectedModelId}
                  disabled={sending}
                />
              )}
              <button
                type="button"
                className={styles.newBtn}
                onClick={openHistory}
                title="Your past conversations"
              >
                📚 History
              </button>
              <button
                type="button"
                className={styles.newBtn}
                onClick={startNew}
                disabled={sending}
              >
                New chat
              </button>
            </div>
          </header>

          <div className={styles.log} ref={logRef}>
            {turns.length === 0 && (
              <div className={styles.intro}>
                <div className={styles.introTitle}>Ask Merlin Analyst 📊</div>
                Ask anything about the business data — customers, orders, quotes,
                invoices, accounts receivable, response times. I read the live data
                (read-only — I never change anything) and explain what I find. Try:
                <ul className={styles.examples}>
                  <li>“Which customers are more than 30 days past due, and how much?”</li>
                  <li>“Top 10 customers by revenue this year vs last.”</li>
                  <li>“How many open orders does Adams Bros have right now?”</li>
                </ul>
              </div>
            )}
            {turns.map((t) =>
              t.role === 'user' ? (
                <div key={t.id} className={styles.userWrap}>
                  <div className={styles.userMsg}>
                    {t.text}
                    <MessageAttachmentChips attachments={t.attachments} />
                  </div>
                  {t.createdAt ? (
                    <div className={styles.userCap}>
                      <MessageTimestamp at={t.createdAt} />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div key={t.id} className={styles.asstWrap}>
                  {/* A server-side recovery note (the thread's history no longer
                      fit the model, so this reply started from a fresh context):
                      calm, not an error. */}
                  {t.notice && (
                    <div className={styles.contextNote} role="status">
                      <span aria-hidden="true">↻</span> {t.notice}
                    </div>
                  )}
                  {(t.blocks.length > 0 || t.streaming) && (
                    <div className={styles.asstMsg}>
                      <AssistantTurn blocks={t.blocks} isStreaming={t.streaming} />
                      {t.streaming && t.blocks.length === 0 && (
                        <span className={styles.thinking}>Looking things up…</span>
                      )}
                    </div>
                  )}
                  {/* Files the agent attached this turn (attach_file) — one
                      downloadable card each (images preview inline). */}
                  {t.agentFiles?.map((file) => (
                    <AgentFileCard
                      key={file.attachmentId}
                      base={ANALYST_BASE}
                      file={file}
                      conversationId={convIdRef.current}
                    />
                  ))}
                  {!t.streaming && t.createdAt ? (
                    <div className={styles.turnStampRow}>
                      <MessageTimestamp at={t.createdAt} />
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </div>

          {error && (
            <div className={styles.errorBar} role="alert">
              {error}
            </div>
          )}

          <div className={styles.composer} {...attach.dropZoneProps}>
            <DropVeil active={attach.isDragging} />
            <AttachmentTray
              files={attach.files}
              attachError={attach.attachError}
              onAddFiles={attach.addFiles}
              onRemoveFile={attach.removeFile}
              disabled={sending}
            />
            <div className={styles.inputRow}>
              <textarea
                className={styles.textarea}
                placeholder="Ask the Analyst about customers, orders, invoices, AR… (drag, paste, or attach files)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={attach.onPaste}
                rows={1}
              />
              <button
                type="button"
                className={styles.sendBtn}
                onClick={submitText}
                disabled={sending || (!input.trim() && attach.files.length === 0)}
              >
                Send
              </button>
            </div>
            <div className={styles.foot}>
              Read-only — the Analyst only reads data, it never changes anything ·
              conversations are saved.
            </div>
          </div>
        </section>

        {/* The history drawer overlays the whole stage (absolute within .stage),
            so its closed state slides fully off the left edge. */}
        <ConversationHistoryDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          conversations={conversations}
          activeId={activeId}
          loading={loadingHistory}
          ownerName={ownerName}
          ownerInitials={ownerInitials}
          onSelect={(id) => void loadConversation(id)}
          onNew={startNew}
        />
      </div>
    </div>
  );
}
